import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BudgetInput } from '../../../components/shared/BudgetInput';
import { ChevronRight, Zap, CheckCircle, Car, Train, Home, Loader2, MapPin, AlertCircle, Clock } from 'lucide-react';
import { useAppContext } from '../../../core/App';
import { useTelemetry } from '../../../core/context/TelemetryContext'; 
import { useCurriculum } from '../../../core/context/CurriculumContext';
import { calculateMarketReality } from '../../../core/api/apiClient';

// Custom input to avoid the "$" prefix on zip codes
const ZipInput = ({ label, value, onChange, placeholder }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input 
            type="text" 
            maxLength="5"
            value={value} 
            onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))} 
            placeholder={placeholder}
            className="block w-full px-3 py-2 sm:text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm outline-none"
        />
    </div>
);

const BudgetStep1 = ({ jobZip, setJobZip, homeZip, setHomeZip, commuteMode, setCommuteMode, takeHome, setTakeHome, onNext, setExpectationDelta }) => {
  const { setToastMessage, userProfile } = useAppContext();
  const { transmitTelemetry } = useTelemetry(); 
  const { resumeReports } = useCurriculum();
  
  const [netPayGuess, setNetPayGuess] = useState('');
  const [hasRevealed, setHasRevealed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  // Extract variables for Market Reality calculation
  const latestReport = resumeReports?.[0] || {};
  const atsScore = latestReport?.overall_score || 50;
  const jobTitle = typeof latestReport?.jobDescription === 'string' ? latestReport.jobDescription.substring(0, 30) : "Entry Level Role";

  const handleCompare = async () => {
    setIsLoading(true);
    try {
      // Extract array of module IDs that have achieved 'mastered' status
      const masteredModules = Object.keys(userProfile?.completedModules || {})
        .filter(id => userProfile.completedModules[id].status === 'mastered');

      const payload = {
          jobZip,
          homeZip,
          commuteMode,
          jobTitle,
          atsScore,
          masteredModules
      };

      const result = await calculateMarketReality(payload);
      
      const actualMonthly = result.net_monthly || 0;
      const guessNum = parseFloat(netPayGuess);

      if (actualMonthly > 0) {
          const percentOff = Math.abs((guessNum - actualMonthly) / actualMonthly);
          if (setExpectationDelta) setExpectationDelta(percentOff);

          if (percentOff <= 0.05) {
            if (transmitTelemetry) {
                transmitTelemetry({
                    moduleId: 'INTEGRITY_BONUS',
                    rawInteractions: [{ status: 'success', type: 'financial_intuition_verified' }]
                });
            }
            setToastMessage("Integrity Bonus: Your financial intuition is within 5% of market reality!");
          }
      }

      setTakeHome(actualMonthly);
      setAnalysisResults(result);
      setHasRevealed(true);
    } catch (error) {
      console.error(error);
      setToastMessage("Error calculating market reality. Please check connection.");
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = jobZip.length === 5 && homeZip.length === 5 && netPayGuess;

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter">Financial Blind Audit</h2>
        <p className="text-slate-500 mt-2 text-sm">Most graduates overestimate their actual take-home pay by 20% and ignore commute costs. Let's test your logistical awareness.</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <ZipInput label="Job Zip Code" value={jobZip} onChange={setJobZip} placeholder="e.g. 10001" />
        <ZipInput label="Home Zip Code" value={homeZip} onChange={setHomeZip} placeholder="e.g. 11201" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Commute Mode</label>
        <div className="flex gap-2">
            {['drive', 'transit', 'wfh'].map((mode) => (
                <button 
                    key={mode}
                    onClick={() => setCommuteMode(mode)}
                    className={`flex-1 p-3 rounded-xl border-2 font-bold uppercase text-xs transition-all
                        ${commuteMode === mode ? 'bg-blue-600 border-blue-400 text-white shadow-md' : 'bg-white border-gray-200 text-gray-500 hover:border-blue-200'}`}
                >
                    {mode === 'drive' && <Car size={16} className="mx-auto mb-1" />}
                    {mode === 'transit' && <Train size={16} className="mx-auto mb-1" />}
                    {mode === 'wfh' && <Home size={16} className="mx-auto mb-1" />}
                    {mode}
                </button>
            ))}
        </div>
      </div>

      {!hasRevealed && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 bg-amber-50 border-2 border-dashed border-amber-200 rounded-2xl">
          <label className="block text-sm font-black text-amber-800 uppercase tracking-widest mb-3">The Perception Guess</label>
          <p className="text-xs text-amber-700 mb-4 italic">"Based on a {jobTitle} role commuting via {commuteMode} from {homeZip || 'your home'} to {jobZip || 'work'}, what is your estimated 'Net Pay' after Federal, State, FICA, and logistical taxes?"</p>
          <BudgetInput 
            label="Your Estimated Monthly Take-Home" 
            id="guess" 
            value={netPayGuess} 
            onChange={setNetPayGuess} 
          />
        </motion.div>
      )}

      <button 
        onClick={handleCompare} 
        disabled={!isFormValid || hasRevealed || isLoading}
        className="w-full flex items-center justify-center bg-slate-900 text-white font-black py-4 rounded-xl shadow-lg hover:bg-black transition disabled:opacity-50"
      >
        {isLoading ? <Loader2 className="mr-2 animate-spin" size={20} /> : (hasRevealed ? <CheckCircle className="mr-2" size={20} /> : <Zap className="mr-2" fill="currentColor" size={20} />)}
        {isLoading ? "Querying Logistics Matrix..." : (hasRevealed ? "Reality Verified" : "Compare Guess to Reality")}
      </button>
      
      {hasRevealed && analysisResults && (
        <motion.div className="mt-6 p-6 bg-slate-900 text-white rounded-2xl border border-slate-700 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xs font-black uppercase tracking-widest text-cyan-400">Reality Audit Result</h3>
                <span className="bg-emerald-500 text-white text-[10px] font-black px-2 py-1 rounded">VERIFIED</span>
            </div>

            <div className="space-y-4">
                {/* 1. Job Title Context */}
                <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-xs text-slate-400">Target Role:</span>
                    <span className="text-xs font-bold truncate max-w-[50%] text-right">{jobTitle}</span>
                </div>

                {/* 2. Market Median */}
                <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-xs text-slate-400">Median for {jobZip}:</span>
                    <span className="text-xs font-bold">${analysisResults.breakdown?.market_median?.toLocaleString()}/yr</span>
                </div>

                {/* 3. ATS Impact */}
                <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-xs text-slate-400">ATS Impact ({atsScore}/100):</span>
                    <span className={`text-xs font-bold ${atsScore >= 90 ? 'text-emerald-400' : (atsScore < 70 ? 'text-red-400' : 'text-slate-300')}`}>
                        {atsScore >= 90 ? `+$${analysisResults.breakdown?.ats_impact?.toLocaleString()} (Premium)` : (atsScore < 70 ? `-$${analysisResults.breakdown?.ats_impact?.toLocaleString()} (Skill Gap)` : `$0 (Median Standard)`)}
                    </span>
                </div>

                {/* 4. HCOL Assessment */}
                <div className="flex justify-between border-b border-white/10 pb-2">
                    <span className="text-xs text-slate-400">Market Class ({homeZip}):</span>
                    <span className="text-xs font-bold text-orange-400">{analysisResults.market_class}</span>
                </div>
            </div>

            {/* Final Take-Home */}
            <div className="mt-6 text-center">
                <p className="text-[10px] text-slate-500 uppercase font-bold">Estimated Monthly Net Pay</p>
                <p className="text-4xl font-black text-emerald-400">${takeHome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>
            
            {/* Logistics Tax Warning */}
            <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl flex items-start gap-3">
                <AlertCircle size={16} className="text-orange-400 mt-0.5 flex-shrink-0" />
                <div>
                    <p className="text-[10px] font-black text-orange-400 uppercase tracking-widest">Commute Alert</p>
                    <p className="text-[10px] text-orange-200 leading-snug">{analysisResults.commute_warning}</p>
                </div>
            </div>

            {/* Traffic Strategy */}
            {analysisResults.traffic_strategy && (
                <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-start gap-3">
                    <Clock size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Strategic Timing</p>
                        <p className="text-[10px] text-blue-100 leading-snug">{analysisResults.traffic_strategy}</p>
                    </div>
                </div>
            )}
            
            <button 
                onClick={onNext} 
                className="w-full flex items-center justify-center bg-emerald-600 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:bg-emerald-700 transition transform hover:scale-[1.02] mt-6"
            >
                Continue to Expenses
                <ChevronRight className="w-5 h-5 ml-2" />
            </button>
        </motion.div>
      )}
    </div>
  );
};

export default BudgetStep1;